import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  TrackByFunction,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntil, withLatestFrom } from 'rxjs';
import { ASPECT_RATIO_CONFIG, episodeListLayoutLocalStorageKey } from '../../../common';
import { ImgQuery, ScreenService } from '../../services';
import { AbstractGatewayService } from '../../services/abstract-gateway.service';
import { AbstractLocalStorageService } from '../../services/abstract-local-storage.service';
import { createFormControl, DestroyableBase } from '../../utils';
import { EpisodeListLayoutType } from '../episode-list-layout-toggle/episode-list-layout-toggle.component';
import { AbstractPlayButtonService } from '../play-button/abstract-play-button.service';
import { EpisodeListViewModel, EpisodeViewModel } from './episode-list-view.model';

type EpisodeListLayoutSettingsForUsers = Record<string, string>;

@Component({
  selector: 'osn-shared-episode-list',
  templateUrl: './episode-list.component.html',
  styleUrls: ['./episode-list.component.scss'],
  animations: [
    trigger('expandAnimation', [
      state('close', style({ height: '60px' })),
      state('open', style({ height: '*' })),
      transition('open <=> close', animate('500ms ease-in-out')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EpisodeListComponent extends DestroyableBase implements OnInit, AfterViewInit {
  @Input() public vm: EpisodeListViewModel;
  @Output() public endReached = new EventEmitter<void>();
  @Output() public episodeClick = new EventEmitter<EpisodeViewModel>();
  @Output() public marketingBannerButtonClick = new EventEmitter<string>();
  @Output() public episodeLayoutChange = new EventEmitter<string>();
  public episodeLayoutControl = createFormControl<EpisodeListLayoutType>('list');
  public openedEpisodeListItem: EpisodeViewModel;
  public aspectRatio = ASPECT_RATIO_CONFIG.landscape;
  public observer = new IntersectionObserver(([entry]) => this.onIntersect(entry));
  public imgQuery: ImgQuery = {
    XS: 430,
    SM: 430,
    MD: 430,
    LG: 430,
    XL: 430,
  };
  private readonly episodeCountLowerThreshold = 4;
  private readonly smallEpisodeCountBannerPosition = 1;
  private readonly smallEpisodeCountUpperThreshold = 7;
  private readonly defaultBannerPosition = 3;
  public get bannerPosition(): number | undefined {
    if (this.vm.episodes.length <= this.episodeCountLowerThreshold) {
      return undefined;
    }
    if (this.vm.episodes.length <= this.smallEpisodeCountUpperThreshold) {
      return this.smallEpisodeCountBannerPosition;
    }
    return this.defaultBannerPosition;
  }
  public isMobile$ = this.screenService.isXS$;

  @ViewChild('intersectionBoundary') public intersectionBoundary: ElementRef;
  @ViewChildren('episodes') public episodes: QueryList<ElementRef>;

  constructor(
    private readonly screenService: ScreenService,
    private readonly gatewayService: AbstractGatewayService,
    private readonly localStorageService: AbstractLocalStorageService,
    private readonly playService: AbstractPlayButtonService
  ) {
    super();
  }

  public trackByFn: TrackByFunction<EpisodeViewModel> = (ix, it) => it.videoId;

  public ngOnInit(): void {
    this.gatewayService
      .getCurrentProfilePersonaId$()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((personaId) => {
        const episodeLayoutSettings = this.getEpisodeLayoutFromLocalStorage();

        if (episodeLayoutSettings?.[personaId]) {
          this.episodeLayoutControl.setValue(episodeLayoutSettings[personaId]);
        }
      });

    this.episodeLayoutControl.valueChanges
      .pipe(takeUntil(this.onDestroy$), withLatestFrom(this.gatewayService.getCurrentProfilePersonaId$()))
      .subscribe(([episodeListLayout, personaId]) => {
        this.saveEpisodeLayoutSettingsToLocalStorage(personaId, episodeListLayout);
        this.episodeLayoutChange.emit(episodeListLayout);
      });
  }

  public ngAfterViewInit(): void {
    this.episodes.changes.subscribe(() => {
      this.observer.disconnect();
      this.observer.observe(this.intersectionBoundary.nativeElement);
    });
    this.observer.observe(this.intersectionBoundary.nativeElement);
  }

  public onEpisodeGridItemThumbnailClicked(selectedEpisode: EpisodeViewModel): void {
    if (!this.isGridLayout()) {
      return;
    }

    this.episodeClick.emit(selectedEpisode);
    this.playVideo(selectedEpisode);
  }

  public onEpisodeListItemClicked(selectedEpisode: EpisodeViewModel): void {
    if (this.isGridLayout()) {
      return;
    }

    this.episodeClick.emit(selectedEpisode);
    this.playVideo(selectedEpisode);
  }

  public onMarketingBannerButtonClick(buttonName: string): void {
    this.marketingBannerButtonClick.emit(buttonName);
  }

  public isEpisodeAvailable(episode: EpisodeViewModel): boolean {
    return !(episode.availableFrom && episode.availableFrom > new Date());
  }

  public isGridLayout(): boolean {
    return this.episodeLayoutControl.value === 'grid';
  }

  public toggleDescription(event: MouseEvent, episode: EpisodeViewModel): void {
    event.stopPropagation();
    this.openedEpisodeListItem = this.openedEpisodeListItem === episode ? null : episode;
  }

  private playVideo(selectedEpisode: EpisodeViewModel): void {
    if (!this.isEpisodeAvailable(selectedEpisode)) {
      return;
    }
    this.playService.playVideo(selectedEpisode.guid, false, this.vm.seriesTitle);
  }

  private onIntersect(entry: IntersectionObserverEntry): void {
    if (entry.isIntersecting) {
      this.endReached.emit();
    }
  }

  private saveEpisodeLayoutSettingsToLocalStorage(personaId: string, layout: string): void {
    const episodeLayoutSettings = this.getEpisodeLayoutFromLocalStorage();
    this.localStorageService.setItem(
      episodeListLayoutLocalStorageKey,
      episodeLayoutSettings ? { ...episodeLayoutSettings, [personaId]: layout } : { [personaId]: layout }
    );
  }

  private getEpisodeLayoutFromLocalStorage(): EpisodeListLayoutSettingsForUsers {
    return this.localStorageService.getItem(episodeListLayoutLocalStorageKey) as EpisodeListLayoutSettingsForUsers;
  }
}
